/**
 * Scan file validation utilities
 * Handles parsing and validation of different scan file formats (Faro, Leica, Geoslam)
 */

/**
 * Available scan type options for the dropdown
 */
export const SCAN_TYPE_OPTIONS = [
  { text: 'Faro Scene', value: 'faro' },
  { text: 'Leica Cyclone', value: 'leica' },
  { text: 'GPS Scan', value: 'gps' },
  { text: 'Geoslam', value: 'geoslam' },
];

/**
 * Get the display label for a scan type value
 * @param {string} scanType - The scan type value
 * @returns {string} The display label
 */
export function getScanTypeLabel(scanType) {
  const option = SCAN_TYPE_OPTIONS.find(o => o.value === scanType);
  return option ? option.text : scanType;
}

/**
 * Read file content as text using FileReader
 * @param {File} file - The file to read
 * @returns {Promise<string>} The file content as text
 */
export function readScanFileContent(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

/**
 * Extract image names from scan file content based on scan type
 * @param {string} content - The scan file text content
 * @param {string} scanType - The type of scan file ('faro', 'leica', 'geoslam')
 * @returns {string[]} Array of unique image names found in the scan file
 */
export function extractImageNamesFromScan(content, scanType) {
  const imageNames = [];
  const lines = content.split('\n');

  if (scanType === 'faro') {
    // Faro format: Each scan line starts with quoted image name
    // Example: "A-DECK-121124-Setup-006" 21.308... (coordinates follow)
    for (const line of lines) {
      const trimmedLine = line.trim();
      // Match lines starting with a quoted string (the image name)
      const match = trimmedLine.match(/^"([^"]+)"/);
      if (match && match[1] && !match[1].startsWith('#')) {
        // Skip comment lines like "# Aiva Point Cloud Utility"
        imageNames.push(match[1]);
      }
    }
  } else if (scanType === 'leica') {
    // Leica Cyclone CSV format
    // Header: SetupName,Position(X),Position(Y),Position(Z),Orientation(W),...
    // Data: A-DECK-121124-Setup-013,17.978...,7.226...,11.231...
    let isFirstLine = true;
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;
      // Skip header line
      if (isFirstLine) {
        isFirstLine = false;
        if (trimmedLine.includes(',') && /position|orientation|setup/i.test(trimmedLine)) {
          continue;
        }
      }
      // Extract first column (comma-separated)
      const firstColumn = trimmedLine.split(',')[0].trim();
      if (firstColumn && firstColumn.length > 2) {
        imageNames.push(firstColumn);
      }
    }
  } else if (scanType === 'geoslam') {
    // Geoslam format - first part before space
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine || trimmedLine.startsWith('#')) continue;
      const match = trimmedLine.match(/^"?([^"\s]+)"?\s/);
      if (match && match[1].length > 2) {
        imageNames.push(match[1]);
      }
    }
  }

  // Remove duplicates
  return [...new Set(imageNames)];
}

/**
 * Validation result object structure
 * @typedef {Object} ValidationResult
 * @property {boolean} isValidating - Whether validation is in progress
 * @property {boolean} hasValidated - Whether validation has completed
 * @property {number} matched - Number of images that matched
 * @property {number} missingInScan - Number of selected images not found in scan
 * @property {number} missingImages - Number of scan entries without corresponding images
 * @property {string[]} missingInScanNames - Names of images not found in scan
 * @property {string} alertType - Alert type: 'success', 'warning', or 'error'
 */

/**
 * Create initial validation state object
 * @returns {ValidationResult}
 */
export function createInitialValidationState() {
  return {
    isValidating: false,
    hasValidated: false,
    matched: 0,
    missingInScan: 0,
    missingImages: 0,
    missingInScanNames: [],
    alertType: 'success',
  };
}

/**
 * Validate selected images against scan file content
 * @param {string} scanFileContent - The scan file text content
 * @param {FileList|File[]} images - The selected image files
 * @param {string} scanType - The type of scan file
 * @returns {ValidationResult} The validation results
 */
export function validateFilesAgainstScan(scanFileContent, images, scanType) {
  // Extract image names from scan file based on scan type
  const scanImageNames = extractImageNamesFromScan(scanFileContent, scanType);

  // Get selected image names (without extension)
  const selectedImageNames = Array.from(images).map(file =>
    file.name.split('.').slice(0, -1).join('.').toLowerCase()
  );

  // Calculate matches (case-insensitive)
  const scanNamesLower = scanImageNames.map(n => n.toLowerCase());
  const matched = selectedImageNames.filter(name => scanNamesLower.includes(name));
  const missingInScanNames = selectedImageNames.filter(name => !scanNamesLower.includes(name));
  const missingImages = scanNamesLower.filter(name => !selectedImageNames.includes(name));

  // Determine alert type based on results
  let alertType = 'success';
  if (missingInScanNames.length > 0) {
    alertType = missingInScanNames.length === selectedImageNames.length ? 'error' : 'warning';
  }

  return {
    isValidating: false,
    hasValidated: true,
    matched: matched.length,
    missingInScan: missingInScanNames.length,
    missingImages: missingImages.length,
    missingInScanNames: missingInScanNames,
    alertType: alertType,
  };
}

/**
 * Format file size to human-readable string
 * @param {number} size - File size in bytes
 * @returns {string} Formatted size string
 */
export function formatFileSize(size) {
  if (size < 1024) {
    return size + ' B';
  } else if (size < 1024 * 1024) {
    return (size / 1024).toFixed(1) + ' KB';
  } else {
    return (size / (1024 * 1024)).toFixed(1) + ' MB';
  }
}
